import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  AUTH_EXPIRED_EVENT,
  AuthStatus,
  api,
  clearToken,
  getStoredToken,
  isError,
  storeToken,
  tokenLifetime,
} from "../api";

interface AuthContextValue {
  status: AuthStatus | null;
  loading: boolean;
  // True once /auth/status has returned at least once. Lets routes wait
  // for the initial check before deciding whether to redirect.
  ready: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<string | null>;
  register: (email: string, password: string) => Promise<string | null>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    const s = await api.authStatus();
    setLoading(false);
    setReady(true);
    if (!isError(s)) {
      setStatus(s);
    } else {
      // Network down or backend not reachable — still mark ready so the
      // UI shows the login page (with the error visible) rather than
      // spinning forever.
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Listen for the api-layer 401 dispatch. When a polling request comes
  // back unauthorised mid-session, clear the stored token and re-check
  // /auth/status — the next render of RequireAuth then bounces the user
  // to /login with the current path preserved in state.from for
  // post-login bounce-back. Guarded against duplicate fires from
  // concurrent polls (the second concurrent 401 sees no token and
  // no-ops).
  useEffect(() => {
    function onExpired() {
      if (getStoredToken() == null) return;
      clearToken();
      void refresh();
    }
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, [refresh]);

  // Sliding session. A token is renewed in the background once it is past
  // half its lifetime, checked every minute and whenever the tab regains
  // focus (a laptop coming back from sleep is the common case). An open
  // dashboard therefore never logs out on its own; a closed one expires
  // after auth.jwt_expires_minutes.
  useEffect(() => {
    let inFlight = false;
    async function maybeRenew() {
      if (inFlight || getStoredToken() == null) return;
      const life = tokenLifetime();
      if (!life) return;
      const now = Date.now() / 1000;
      const half = life.iat + (life.exp - life.iat) / 2;
      if (now < half || now >= life.exp) return;
      inFlight = true;
      try {
        const r = await api.authRefresh();
        if (!isError(r)) storeToken(r.access_token);
      } finally {
        inFlight = false;
      }
    }
    void maybeRenew();
    const id = window.setInterval(() => void maybeRenew(), 60_000);
    const onFocus = () => void maybeRenew();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<string | null> => {
      const r = await api.authLogin(email, password);
      if (isError(r)) return r.error;
      storeToken(r.access_token);
      await refresh();
      return null;
    },
    [refresh],
  );

  const register = useCallback(
    async (email: string, password: string): Promise<string | null> => {
      const r = await api.authRegister(email, password);
      if (isError(r)) return r.error;
      // First-user bootstrap: immediately log in with the same creds so
      // the user lands on the dashboard rather than having to log in again.
      return login(email, password);
    },
    [login],
  );

  const logout = useCallback(() => {
    clearToken();
    void refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider
      value={{ status, loading, ready, refresh, login, register, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

/** True iff a logged-in user is present in the most recent /auth/status. */
export function useIsAuthenticated(): boolean {
  const { status } = useAuth();
  return status?.current_user != null;
}

/** True when auth is fully wired (JWT_SECRET set AND at least one user). */
export function useAuthEnabled(): boolean {
  const { status } = useAuth();
  return status?.enabled === true;
}

/** True when JWT_SECRET is set but no user exists yet (setup needed). */
export function useNeedsSetup(): boolean {
  const { status } = useAuth();
  return (
    status?.jwt_configured === true && (status?.user_count ?? 0) === 0
  );
}

/** Re-export so other components don't import from api.ts directly. */
export { getStoredToken };
